/* Per-venue brand theming.
 * The myREST Design System ships a fixed violet→magenta brand gradient, but the
 * platform is multi-tenant: each venue can set its own primary colour
 * (companyData.colors.main). This helper derives a full token override — primary
 * shades, a soft tint, a 3-stop on-brand gradient and the CTA glow — from a single
 * venue hex, so the whole UI re-colours to the venue while keeping the design's
 * shape. With no venue colour the app falls back to the myREST defaults baked into
 * scss/tokens/_colors.scss. */

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export function normalizeHex(input) {
  if (typeof input !== 'string') return null;
  let hex = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    hex = hex.split('').map((c) => c + c).join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
  return '#' + hex.toLowerCase();
}

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

function hslToHex(h, s, l) {
  h = ((h % 360) + 360) % 360;
  s = clamp(s, 0, 100) / 100;
  l = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) { r = c; g = x; }
  else if (h < 120) { r = x; g = c; }
  else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; }
  else if (h < 300) { r = x; b = c; }
  else { r = c; b = x; }
  const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return '#' + toHex(r) + toHex(g) + toHex(b);
}

function withLightness(hsl, l) {
  return hslToHex(hsl.h, hsl.s, l);
}

function rgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Build a `:root { … }` CSS string overriding the brand tokens for a venue hex.
 * Returns '' when the colour is missing/invalid so callers can skip injection.
 */
export function brandThemeCss(rawColor) {
  const base = normalizeHex(rawColor);
  if (!base) return '';

  const hsl = hexToHsl(base);
  const hover = withLightness(hsl, clamp(hsl.l - 8, 6, 94));
  const active = withLightness(hsl, clamp(hsl.l - 16, 4, 92));
  const soft = hslToHex(hsl.h, clamp(hsl.s, 0, 70), 95);

  // Monochromatic sheen gradient: same hue, only lightness varies. Keeps the CTA
  // cohesive for ANY tenant colour (a big hue rotation would produce a jarring
  // light band, e.g. orange → yellow, at the edge).
  const gradDark = withLightness(hsl, clamp(hsl.l - 5, 6, 90));
  const gradLight = withLightness(hsl, clamp(hsl.l + 8, 12, 94));
  const gradient = `linear-gradient(120deg, ${gradDark} 0%, ${base} 52%, ${gradLight} 100%)`;

  return `:root{`
    + `--color-primary:${base};`
    + `--color-primary-hover:${hover};`
    + `--color-primary-active:${active};`
    + `--color-primary-soft:${soft};`
    + `--text-link:${base};`
    + `--grad-brand:${gradient};`
    + `--shadow-brand:0 12px 28px ${rgba(base, 0.3)};`
    + `--sm-glow:${rgba(base, 0.3)};`
    + `--focus-ring:${rgba(base, 0.35)};`
    + `}`;
}

export default brandThemeCss;
